/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { addonService } from '@/services/billing/addon.service';
import type {
  CreateFeatureAddonRequest,
  UpdateFeatureAddonRequest,
} from '@/types/addon.types';
import { myModulesKeys } from './useModuleFeatures';
import { menuKeys } from '@/modules/venta/hooks/useMenus';

export const addonKeys = {
  all: ['addons'] as const,
  active: () => [...addonKeys.all, 'active'] as const,
  available: (moduleCode: string) => [...addonKeys.all, 'available', moduleCode] as const,
  admin: () => [...addonKeys.all, 'admin'] as const,
};

export function useActiveAddons() {
  return useQuery({
    queryKey: addonKeys.active(),
    queryFn: () => addonService.getActiveAddons(),
    staleTime: 60_000,
  });
}

export function useAvailableAddons(moduleCode: string) {
  return useQuery({
    queryKey: addonKeys.available(moduleCode),
    queryFn: () => addonService.getAvailableAddons(moduleCode),
    enabled: !!moduleCode,
    staleTime: 60_000,
  });
}

export function usePurchaseAddon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleCode, featureCode }: { moduleCode: string; featureCode: string }) =>
      addonService.purchaseAddon(moduleCode, featureCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: addonKeys.all });
      qc.invalidateQueries({ queryKey: myModulesKeys.all });
      qc.invalidateQueries({ queryKey: menuKeys.myMenus() });
    },
  });
}

export function useUpdateAddonUserCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleCode, featureCode, userCount }: { moduleCode: string; featureCode: string; userCount: number }) =>
      addonService.updateAddonUserCount(moduleCode, featureCode, userCount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: addonKeys.all });
    },
  });
}

export function useCancelAddon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleCode, featureCode, reason }: { moduleCode: string; featureCode: string; reason?: string }) =>
      addonService.cancelAddon(moduleCode, featureCode, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: addonKeys.all });
      qc.invalidateQueries({ queryKey: myModulesKeys.all });
      qc.invalidateQueries({ queryKey: menuKeys.myMenus() });
    },
  });
}

export function useAdminAddons() {
  return useQuery({
    queryKey: addonKeys.admin(),
    queryFn: () => addonService.adminListAddons(),
    staleTime: 30_000,
  });
}

export function useAdminCreateAddon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFeatureAddonRequest) => addonService.adminCreateAddon(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: addonKeys.admin() });
    },
  });
}

export function useAdminUpdateAddon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFeatureAddonRequest }) =>
      addonService.adminUpdateAddon(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: addonKeys.admin() });
    },
  });
}

export function useAdminDeactivateAddon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => addonService.adminDeactivateAddon(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: addonKeys.admin() });
    },
  });
}
